import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { postApi } from '../../utils/apiService';
import { GET_ASSESSMENT_TEMPLATE, ADD_STUDENT_ASSESSMENT } from '../../utils/apiPath';
import Button from '../../components/Button';
import {
    ChevronRight,
    ChevronLeft,
    CheckCircle,
    Clock,
    AlertTriangle,
    X,
    ClipboardList,
    ListChecks,
    Layers,
    Award,
    ArrowRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader/Loader';
import { APP_LOGO as logo } from '../../utils/appAssets';
import './Assessment.scss';
import './grid-layout.scss';

const ASSESSMENT_SESSION_KEYS = [
    'assessmentStudentData',
    'assessmentStartTime',
    'assessmentDurationMin',
    'assessmentGenerateInstruction',
    'assessmentInstructionsAck'
];

// Candidate field names the API may use for a question's mark weight. We read the
// first one that holds a valid number so the instructions stay correct regardless
// of the exact key the backend returns.
const QUESTION_MARK_FIELDS = ['marks', 'mark', 'questionMarks', 'weightage', 'score'];

const getQuestionMarks = (question) => {
    for (const field of QUESTION_MARK_FIELDS) {
        const value = question?.[field];
        if (value !== undefined && value !== null && value !== '' && !Number.isNaN(Number(value))) {
            return Number(value);
        }
    }
    return null;
};

// Flatten a module's questions into the answerable "leaf" questions. Comprehension
// parents (questionType 6) are represented by their child questions in the count.
const getLeafQuestions = (questions = []) => {
    const leaves = [];
    questions.forEach((q) => {
        if (q.questionType === 6 && Array.isArray(q.childQuestions) && q.childQuestions.length > 0) {
            leaves.push(...getLeafQuestions(q.childQuestions));
        } else {
            leaves.push(q);
        }
    });
    return leaves;
};

// Derive the paper composition (total questions, per-module ranges, marks) straight
// from the GetAssessmentTemplate `modules` object so the General Instructions screen
// always reflects the real template.
const buildInstructionData = (template) => {
    const modules = Array.isArray(template?.modules) ? template.modules : [];
    if (modules.length === 0) {
        return { totalQuestions: 0, totalMarks: 0, uniformMarks: null, hasMarks: false, moduleBreakdown: [] };
    }

    const orderedModules = [...modules].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const distinctMarks = new Set();
    let runningCount = 0;
    let totalMarks = 0;

    const moduleBreakdown = orderedModules.map((module) => {
        const leaves = getLeafQuestions(module.questions);
        const count = leaves.length;
        const start = runningCount + 1;
        const end = runningCount + count;
        runningCount = end;

        leaves.forEach((leaf) => {
            const marks = getQuestionMarks(leaf);
            if (marks !== null) {
                totalMarks += marks;
                distinctMarks.add(marks);
            }
        });

        // Negative marking is authored per module. Only treat it as active when the
        // value is a valid number greater than 0.
        const rawNegative = Number(module.negativeMarksPerQuestion);
        const negativeMarksPerQuestion =
            !Number.isNaN(rawNegative) && rawNegative > 0 ? rawNegative : 0;

        return { name: module.name, count, start, end, negativeMarksPerQuestion };
    });

    return {
        totalQuestions: runningCount,
        totalMarks,
        hasMarks: distinctMarks.size > 0,
        uniformMarks: distinctMarks.size === 1 ? [...distinctMarks][0] : null,
        moduleBreakdown
    };
};

const pluralize = (count, singular, plural) => (Number(count) === 1 ? singular : (plural || `${singular}s`));

const TIMER_ALERTS = {
    warning: {
        title: '5 minutes remaining',
        message: 'Please review your responses and complete this module. Your assessment will submit automatically when the timer ends.'
    },
    expired: {
        title: 'Time is up',
        message: 'Your assessment is being submitted automatically. Please wait while we save your responses.'
    }
};

const ASSESSMENT_LOADING_CONTENT = {
    default: {
        badge: 'Meridian Assessment',
        title: 'Preparing your assessment',
        subtitle: 'We are setting up your questions, timer, and modules so you can begin smoothly.',
        tips: [
            'Read each question carefully before selecting your response.',
            'Stay calm and answer honestly. Consistent responses are more useful than rushed ones.',
            'If a module feels difficult, focus on one question at a time and keep moving forward.'
        ]
    },
    submit: {
        badge: 'Saving Progress',
        title: 'Submitting your assessment',
        subtitle: 'Your responses are being securely saved. Please stay on this screen while we complete the submission.',
        tips: [
            'Taking time to reflect before answering helps build better self-awareness.',
            'Progress matters more than perfection. Every completed assessment supports your growth.',
            'A calm mind improves decision-making. A slow breath can help you reset your focus.'
        ]
    }
};

const Assessment = () => {
    const navigate = useNavigate();
    const { assessmentTypeId, gradeId, scheduleId, branchId } = useParams();
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("Loading Assessment...");
    const [template, setTemplate] = useState(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [studentData, setStudentData] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [direction, setDirection] = useState('next');
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [remainingSeconds, setRemainingSeconds] = useState(null);
    const [timerExpired, setTimerExpired] = useState(false);
    const [timerAlertState, setTimerAlertState] = useState(null);
    const [submissionResult, setSubmissionResult] = useState(null);
    // Show the General Instructions screen first only when the schedule opted into it
    // (flag carried from Home) and the student hasn't already acknowledged it — the
    // acknowledgement guard keeps a page refresh from re-gating an in-progress test.
    const [showInstructions, setShowInstructions] = useState(
        () =>
            sessionStorage.getItem('assessmentGenerateInstruction') === 'true' &&
            sessionStorage.getItem('assessmentInstructionsAck') !== 'true'
    );
    const autoSubmitCalled = useRef(false);
    const fiveMinuteAlertShown = useRef(false);
    const submitInProgressRef = useRef(false);
    const moduleContentRef = useRef(null);

    // Countdown Timer
    useEffect(() => {
        // Hold the clock while the General Instructions screen is up — the timer only
        // starts once the student proceeds into the actual assessment.
        if (submissionResult || showInstructions) return undefined;

        const startTimeStr = sessionStorage.getItem('assessmentStartTime');
        if (!startTimeStr) return;

        const startTime = parseInt(startTimeStr, 10);
        const durationMin = parseInt(sessionStorage.getItem('assessmentDurationMin') || '0', 10);
        const totalSeconds = durationMin * 60;

        const tick = () => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            setElapsedSeconds(elapsed);

            if (totalSeconds > 0) {
                const remaining = Math.max(0, totalSeconds - elapsed);
                setRemainingSeconds(remaining);
                if (remaining === 0) setTimerExpired(true);
            }
        };

        tick();
        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [submissionResult, showInstructions]);

    // Auto-submit when countdown reaches zero
    useEffect(() => {
        if (timerExpired && !loading && template && !submissionResult && !autoSubmitCalled.current) {
            autoSubmitCalled.current = true;
            setTimerAlertState('expired');
            toast.warning("Time is up. Your assessment is being submitted automatically.", {
                toastId: 'assessment-time-expired'
            });
            handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timerExpired, loading, submissionResult, template]);

    useEffect(() => {
        if (
            remainingSeconds !== null &&
            remainingSeconds > 0 &&
            remainingSeconds <= 300 &&
            !fiveMinuteAlertShown.current
        ) {
            fiveMinuteAlertShown.current = true;
            setTimerAlertState('warning');
            toast.warning("Only 5 minutes left. Please review and complete your assessment.", {
                toastId: 'assessment-five-minute-warning'
            });
        }
    }, [remainingSeconds]);

    const formatTime = (totalSeconds) => {
        const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const s = (totalSeconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    // Reads fresh from sessionStorage at submission time to avoid stale closure issues.
    // Time taken = total duration - remaining time (or elapsed if no duration limit).
    const computeTimeTaken = () => {
        const startTimeStr = sessionStorage.getItem('assessmentStartTime');
        const durationMin = parseInt(sessionStorage.getItem('assessmentDurationMin') || '0', 10);

        if (!startTimeStr) return formatTime(elapsedSeconds);

        const elapsed = Math.floor((Date.now() - parseInt(startTimeStr, 10)) / 1000);

        if (durationMin > 0) {
            const totalSeconds = durationMin * 60;
            return formatTime(Math.min(elapsed, totalSeconds));
        }

        return formatTime(elapsed);
    };

    useEffect(() => {
        const storedData = sessionStorage.getItem('assessmentStudentData');
        if (!storedData) {
            toast.error("Student details missing. Please start from Home.");
            navigate('/');
            return;
        }
        setStudentData(JSON.parse(storedData));

        const fetchData = async () => {
            try {
                const response = await postApi(GET_ASSESSMENT_TEMPLATE, {
                    gradeId: gradeId,
                    assessmentTypeId: assessmentTypeId,
                    assessmentTypeScheduleId: scheduleId,
                });
                // Full template payload — used to generate the General Instructions
                // from the `modules` object (question counts, ranges and marks).
                console.log('GetAssessmentTemplate response:', response);
                if (response?.data?.modules) {
                    const modules = response.data.modules.sort((a, b) => a.sortOrder - b.sortOrder);
                    setTemplate({ ...response.data, modules });
                    setLoading(false);
                } else {
                    throw new Error("Invalid template data");
                }
            } catch (error) {
                console.error("Failed to fetch template", error);
                toast.error("Failed to load assessment. Please try again.");
            }
        };
        fetchData();
    }, [assessmentTypeId, gradeId, navigate]);

    const handleAnswerChange = (questionId, value) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const currentModule = template?.modules[currentModuleIndex];

    // Compose the General Instructions purely from the loaded template's modules.
    const instructionData = useMemo(() => buildInstructionData(template), [template]);

    // Duration is authored on the schedule and stashed in sessionStorage by Home.
    const durationMin = parseInt(sessionStorage.getItem('assessmentDurationMin') || '0', 10);

    // Build the numbered "General Instructions" lines from the derived paper composition.
    const generalInstructions = useMemo(() => {
        const { totalQuestions, totalMarks, uniformMarks, hasMarks, moduleBreakdown } = instructionData;
        const lines = [];

        if (totalQuestions > 0) {
            lines.push(`This question paper consists of ${totalQuestions} ${pluralize(totalQuestions, 'question')}.`);
        }

        if (uniformMarks !== null) {
            lines.push(`Each question carries ${uniformMarks} ${pluralize(uniformMarks, 'mark')}.`);
        } else if (hasMarks && totalMarks > 0) {
            lines.push('Marks vary by question. Refer to each module for the marks allotted.');
        }

        moduleBreakdown.forEach((module) => {
            if (module.count <= 0) return;
            const range =
                module.count === 1
                    ? `Question Number ${module.start} is`
                    : `Question Numbers ${module.start} to ${module.end} are`;
            let line = `${range} ${module.name}.`;
            // Surface per-module negative marking only when the module actually carries it.
            if (module.negativeMarksPerQuestion > 0) {
                line += ` This section has negative marking of -${module.negativeMarksPerQuestion} ${pluralize(module.negativeMarksPerQuestion, 'mark')} per question.`;
            }
            lines.push(line);
        });

        if (hasMarks && totalMarks > 0) {
            lines.push(`The assessment carries a total of ${totalMarks} ${pluralize(totalMarks, 'mark')}.`);
        }

        if (durationMin > 0) {
            lines.push(`You have ${durationMin} ${pluralize(durationMin, 'minute')} to complete the assessment.`);
        }

        // Standard conduct guidance that applies to every timed assessment.
        lines.push('Complete every question in a module to unlock the next one.');
        lines.push('Use the Previous and Next buttons to move between modules.');
        lines.push('The assessment is submitted automatically when the timer runs out.');
        lines.push('Do not refresh or close this tab once you begin.');

        return lines;
    }, [instructionData, durationMin]);

    // Student clicked "Proceed" — record the acknowledgement, (re)start the clock from
    // this moment so reading time isn't deducted, and reveal the modules.
    const handleProceedToAssessment = () => {
        sessionStorage.setItem('assessmentInstructionsAck', 'true');
        sessionStorage.setItem('assessmentStartTime', Date.now().toString());
        setShowInstructions(false);
    };

    const isQuestionAnswered = (q) => {
        if (q.questionType === 6) {
            return q.childQuestions && q.childQuestions.length > 0
                ? q.childQuestions.every(cq => isQuestionAnswered(cq))
                : true;
        }

        const answer = answers[q.questionId];

        if (q.questionType === 4) { // Multi Choice
            return Array.isArray(answer) && answer.length > 0;
        }

        return answer !== undefined && answer !== '';
    };

    const isModuleComplete = currentModule?.questions.every(q => isQuestionAnswered(q));

    const scrollModuleContentToTop = () => {
        if (moduleContentRef.current) {
            moduleContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCloseTab = () => {
        // Reassigning to about:blank before closing allows the browser to close
        // tabs that were not opened via window.open().
        window.open('about:blank', '_self');
        window.close();

        // Fallback: if the browser still blocks the close (some strict policies),
        // inform the user to close the tab manually.
        setTimeout(() => {
            toast.info("Please close this tab manually.", {
                toastId: 'close-tab-fallback'
            });
        }, 300);
    };

    const handleNext = () => {
        if (currentModuleIndex < (template?.modules.length || 0) - 1) {
            setDirection('next');
            setCurrentModuleIndex(prev => prev + 1);
            scrollModuleContentToTop();
        }
    };

    const handlePrev = () => {
        if (currentModuleIndex > 0) {
            setDirection('prev');
            setCurrentModuleIndex(prev => prev - 1);
            scrollModuleContentToTop();
        }
    };

    const clearAssessmentSession = () => {
        ASSESSMENT_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
    };

    async function handleSubmit() {
        if (!studentData || !template || submissionResult || submitInProgressRef.current) return;

        autoSubmitCalled.current = true;
        submitInProgressRef.current = true;
        setLoadingText("Submitting Assessment...");
        setLoading(true);

        const formattedAnswers = [];
        const timeTaken = computeTimeTaken();

        const collectAnswers = (questions) => {
            questions.forEach(q => {
                if (q.questionType === 6) {
                    if (q.childQuestions) collectAnswers(q.childQuestions);
                    return;
                }

                const answerValue = answers[q.questionId];
                if (answerValue !== undefined) {
                    let payloadItem = {
                        questionId: q.questionId,
                        responseValue: null,
                        responseValues: [],
                        responseText: ""
                    };

                    if (q.questionType === 3) { // Single Choice
                        const selectedOption = q.options.find(opt => opt.optionId === answerValue);
                        if (selectedOption) {
                            payloadItem.responseValue = selectedOption.optionId;
                        }
                    } else if (q.questionType === 4) { // Multi Choice
                        const selectedOptions = q.options.filter(opt => answerValue.includes(opt.optionId));
                        if (selectedOptions.length > 0) {
                            payloadItem.responseValues = selectedOptions.map(opt => opt.optionId);
                        }
                        payloadItem.responseValue = null;
                    } else if (q.questionType === 1 || q.questionType === 2 || q.questionType === 5) { // Text / Long Text / Date
                        payloadItem.responseText = answerValue;
                    }

                    formattedAnswers.push(payloadItem);
                }
            });
        };

        template.modules.forEach(module => collectAnswers(module.questions));

        const payload = {
            studentName: studentData.studentName,
            className: currentModule?.gradeName || template?.modules?.[0]?.gradeName || '',
            sectionName: studentData.section,
            parentEmailId: studentData.parentEmail,
            academicYear: studentData.academicYear,
            branchName: studentData.branchName || "",
            branchId: studentData.branchId !== undefined ? studentData.branchId : (branchId ? Number(branchId) : null),
            dob: studentData.dob,
            timeTaken,
            answers: formattedAnswers,
            assessmentTypeId: assessmentTypeId ? Number(assessmentTypeId) : null,
            assessmentTypeScheduleId: scheduleId ? Number(scheduleId) : null,
        };

        try {
            const response = await postApi(ADD_STUDENT_ASSESSMENT, payload);

            if (response?.statusCode === 200 && response?.data) {
                clearAssessmentSession();
                setSubmissionResult({ timeTaken });
                toast.success("Assessment submitted successfully.");
            } else {
                throw new Error(response?.message || "Submission failed");
            }
        } catch (error) {
            console.error("Submission failed", error);
            toast.error(error?.message || "Failed to submit assessment. Please try again.");
        } finally {
            submitInProgressRef.current = false;
            setLoading(false);
        }
    }

    const activeLoaderContent =
        loadingText === "Submitting Assessment..."
            ? ASSESSMENT_LOADING_CONTENT.submit
            : ASSESSMENT_LOADING_CONTENT.default;

    if (loading) {
        return (
            <Loader
                text={loadingText}
                badge={activeLoaderContent.badge}
                title={activeLoaderContent.title}
                subtitle={activeLoaderContent.subtitle}
                tips={activeLoaderContent.tips}
            />
        );
    }

    if (submissionResult) {
        const gradeLabel = currentModule?.gradeName || template?.modules?.[0]?.gradeName || 'Grade';

        return (
            <div className="app-container app-container--success">
                <div className="submission-card slide-down">
                    <div className="submission-card__icon-wrap">
                        <CheckCircle size={64} />
                    </div>

                    <span className="submission-card__eyebrow">Assessment Completed</span>
                    <h2 className="submission-card__title">Assessment submitted successfully</h2>
                    <p className="submission-card__message">
                        {`Thank you, ${studentData.studentName}. Your assessment has been submitted successfully.`}
                    </p>

                    <div className="submission-card__stats">
                        <div className="submission-stat">
                            <span className="submission-stat__label">Class</span>
                            <strong>{gradeLabel}{studentData.section ? ` - Sec ${studentData.section}` : ''}</strong>
                        </div>
                        <div className="submission-stat">
                            <span className="submission-stat__label">Academic Year</span>
                            <strong>{studentData.academicYear}</strong>
                        </div>
                        <div className="submission-stat">
                            <span className="submission-stat__label">Time Taken</span>
                            <strong>{submissionResult.timeTaken}</strong>
                        </div>
                    </div>

                    <p className="submission-card__hint">
                        Your report has been generated and will be shared by your school administrator.
                    </p>

                    <div className="submission-card__actions">
                        <Button onClick={handleCloseTab}>
                            <X size={18} />
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (!template || !studentData || !currentModule) {
        return null;
    }

    // General Instructions gate — shown before the modules when the schedule enabled it.
    if (showInstructions) {
        const { totalQuestions, totalMarks, hasMarks, moduleBreakdown } = instructionData;
        return (
            <div className="app-container app-container--instructions">
                <div className="instructions-card slide-down">
                    <div className="instructions-card__header">
                        <img src={logo} alt="Meridian School" className="instructions-card__logo" />
                        <div className="instructions-card__heading">
                            <span className="instructions-card__eyebrow">
                                <ClipboardList size={16} /> General Instructions
                            </span>
                            <h1 className="instructions-card__title">{template.name || 'Student Assessment'}</h1>
                            <p className="instructions-card__subtitle">
                                Please read the instructions below carefully before you begin.
                            </p>
                        </div>
                    </div>

                    <div className="instructions-card__meta">
                        {totalQuestions > 0 && (
                            <div className="instructions-meta-chip">
                                <ListChecks size={18} />
                                <span className="instructions-meta-chip__value">{totalQuestions}</span>
                                <span className="instructions-meta-chip__label">{pluralize(totalQuestions, 'Question')}</span>
                            </div>
                        )}
                        <div className="instructions-meta-chip">
                            <Layers size={18} />
                            <span className="instructions-meta-chip__value">{moduleBreakdown.length}</span>
                            <span className="instructions-meta-chip__label">{pluralize(moduleBreakdown.length, 'Module')}</span>
                        </div>
                        {hasMarks && totalMarks > 0 && (
                            <div className="instructions-meta-chip">
                                <Award size={18} />
                                <span className="instructions-meta-chip__value">{totalMarks}</span>
                                <span className="instructions-meta-chip__label">Total Marks</span>
                            </div>
                        )}
                        {durationMin > 0 && (
                            <div className="instructions-meta-chip">
                                <Clock size={18} />
                                <span className="instructions-meta-chip__value">{durationMin}</span>
                                <span className="instructions-meta-chip__label">{pluralize(durationMin, 'Minute')}</span>
                            </div>
                        )}
                    </div>

                    <ol className="instructions-list">
                        {generalInstructions.map((line, idx) => (
                            <li key={idx} className="instructions-list__item">
                                <span className="instructions-list__number">{idx + 1}</span>
                                <span className="instructions-list__text">{line}</span>
                            </li>
                        ))}
                    </ol>

                    <div className="instructions-card__footer">
                        <p className="instructions-card__note">
                            The timer starts as soon as you proceed. All the best, {studentData.studentName}!
                        </p>
                        <Button onClick={handleProceedToAssessment}>
                            Proceed to Assessment <ArrowRight size={18} />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const progress = ((currentModuleIndex + 1) / template.modules.length) * 100;
    const activeTimerAlert = timerAlertState ? TIMER_ALERTS[timerAlertState] : null;

    const renderQuestionInput = (q) => {
        switch (q.questionType) {
            case 1: // Short Text
                return (
                    <input
                        type="text"
                        className="text-answer-input"
                        placeholder="Type your answer here..."
                        value={answers[q.questionId] || ''}
                        onChange={(e) => handleAnswerChange(q.questionId, e.target.value)}
                    />
                );
            case 2: // Long Text
                return (
                    <textarea
                        className="text-answer-input text-answer-input--long"
                        placeholder="Type your explanation here..."
                        value={answers[q.questionId] || ''}
                        onChange={(e) => handleAnswerChange(q.questionId, e.target.value)}
                    />
                );
            case 4: // Multi Choice
                const currentSelections = Array.isArray(answers[q.questionId]) ? answers[q.questionId] : [];
                return (
                    <div className="choice-group">
                        {q.options.sort((a, b) => a.sortOrder - b.sortOrder).map(opt => (
                            <label key={opt.optionId} className={`choice-label ${currentSelections.includes(opt.optionId) ? 'selected' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={currentSelections.includes(opt.optionId)}
                                    onChange={(e) => {
                                        const newValue = e.target.checked
                                            ? [...currentSelections, opt.optionId]
                                            : currentSelections.filter(id => id !== opt.optionId);
                                        handleAnswerChange(q.questionId, newValue);
                                    }}
                                />
                                <span className="choice-text">{opt.optionText}</span>
                                <div className="choice-indicator checkbox"></div>
                            </label>
                        ))}
                    </div>
                );
            case 5: // Date
                return (
                    <input
                        type="date"
                        className="text-answer-input"
                        value={answers[q.questionId] || ''}
                        onChange={(e) => handleAnswerChange(q.questionId, e.target.value)}
                    />
                );
            case 3: // Single Choice
            default:
                return (
                    <div className="choice-group">
                        {q.options.sort((a, b) => a.sortOrder - b.sortOrder).map(opt => (
                            <label key={opt.optionId} className={`choice-label ${answers[q.questionId] === opt.optionId ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name={q.questionId}
                                    checked={answers[q.questionId] === opt.optionId}
                                    onChange={() => handleAnswerChange(q.questionId, opt.optionId)}
                                />
                                <span className="choice-text">{opt.optionText}</span>
                                <div className="choice-indicator radio"></div>
                            </label>
                        ))}
                    </div>
                );
        }
    };

    const renderQuestion = (q, idx, isChild = false) => {
        return (
            <div key={q.questionId} className={`question-card ${isChild ? 'child-card' : 'main-card'}`} style={{ animationDelay: `${idx * 0.05}s` }}>
                <div className="question-header-row">
                    <div className={`number-bubble ${isChild ? 'child-bubble' : ''}`}>
                        <span className="bubble-text">{isChild ? String.fromCharCode(97 + idx) : idx + 1}</span>
                    </div>
                    <div className="question-text-content">
                        <div className="question-text" style={{ fontWeight: q.questionType === 6 ? '700' : '600' }}>
                            {q.questionText}
                        </div>
                    </div>
                </div>

                {q.questionType === 6 ? (
                    <div className="child-questions-container">
                        {q.childQuestions && q.childQuestions.sort((a, b) => a.sortOrder - b.sortOrder).map((cq, cIdx) =>
                            renderQuestion(cq, cIdx, true)
                        )}
                    </div>
                ) : (
                    <div className="question-body">
                        <div className="question-input-section">
                            {renderQuestionInput(q)}
                        </div>

                        {q.questionMediaUrl ? (
                            <div className="question-media-section">
                                <div className="media-frame">
                                    <img src={q.questionMediaUrl} alt="Question Media" />
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="app-container app-container--assessment">
            {/* Header */}
            <div className="header-container slide-down">
                <div className="header-info">
                    <div>
                        <h2 className="student-name">{studentData.studentName}</h2>
                        <p className="student-details">{currentModule?.gradeName || 'Grade'} {studentData.section ? ` - Sec ${studentData.section}` : ''} • {studentData.academicYear}</p>
                    </div>
                    <div className="module-counter">
                        <span className={`timer-badge timer-badge--${remainingSeconds !== null
                            ? remainingSeconds <= 60 ? 'danger'
                                : remainingSeconds <= 300 ? 'warning'
                                    : 'normal'
                            : 'normal'
                            }`}>
                            <Clock size={16} />
                            {remainingSeconds !== null ? formatTime(remainingSeconds) : formatTime(elapsedSeconds)}
                        </span>
                        <span className="module-counter__label">
                            Module {currentModuleIndex + 1} of {template.modules.length}
                        </span>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="progress-track">
                    <div
                        className="progress-fill"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {activeTimerAlert ? (
                    <div className={`timer-alert timer-alert--${timerAlertState}`} role="alert" aria-live="assertive">
                        <div className="timer-alert__icon">
                            <AlertTriangle size={20} />
                        </div>
                        <div className="timer-alert__content">
                            <p className="timer-alert__title">{activeTimerAlert.title}</p>
                            <p className="timer-alert__message">{activeTimerAlert.message}</p>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Module Content */}
            <div key={currentModule.moduleId} ref={moduleContentRef} className="module-card fade-in-slide">
                <div className="module-header">
                    <h3 className="module-title">{currentModule.name}</h3>
                    {currentModule.description && (
                        <p className="module-instruction">{currentModule.description}</p>
                    )}
                </div>

                <div className="questions-container">
                    {currentModule.questions.map((q, idx) => renderQuestion(q, idx))}
                </div>

                {/* Module Navigation */}
                <div className="module-nav-actions">
                    <Button
                        variant="ghost"
                        onClick={handlePrev}
                        disabled={currentModuleIndex === 0}
                    >
                        <ChevronLeft size={20} /> <span className="btn-text">Previous</span>
                    </Button>

                    {currentModuleIndex === template.modules.length - 1 ? (
                        <Button onClick={handleSubmit} disabled={!isModuleComplete}>
                            Submit <span className="btn-text">Assessment</span> <CheckCircle size={20} />
                        </Button>
                    ) : (
                        <Button onClick={handleNext} disabled={!isModuleComplete}>
                            Next <span className="btn-text">Module</span> <ChevronRight size={20} />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Assessment;
